import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { ISlot } from '../../organisms/bo_calendar_slot/types';
import { ETimes } from '../../types/enums/times';
import { spaceHeight, weekdayHeight } from '../atoms/styles';
import {
	ECalendarMode,
	ElementHTML,
	ISlotEventsToSpaces,
	ISlotsEventsAndEventsWithSpaces,
	IResource,
	ISlotDateGroup,
} from '../types';
import { HOURS_PER_DAY, isDateBetweenDates, MINUTES_PER_HOUR, oneHourHeight } from './timeUtils';
import { chain, sumBy } from 'lodash';
import { DateTimeFormats } from '@bondsports/date-time';
import { calculateOverallEndTime, calculateOverallStartTime, getCombinedDateTimeString } from '../../lib/timeUtils';

dayjs.extend(customParseFormat);

/**
 * Calculating the height of the body container based on the mode and resources
 * @param resources {IResource[]} - resources to calculate the height for
 * @param mode {ECalendarMode} - mode to calculate the height for
 * @param horizontal {boolean} - whether to calculate the height for horizontal mode
 * @return {number} - calculated height of the body container
 */
export function getBodyContainerHeight(resources: IResource[], mode: ECalendarMode, horizontal?: boolean): number {
	if (horizontal) {
		return sumBy(resources, resource => resource.children.length || 1) * spaceHeight;
	}

	if (mode === ECalendarMode.DAILY || mode === ECalendarMode.DAYX) {
		return oneHourHeight * 24 + 12;
	}

	if (mode === ECalendarMode.WEEKLY) {
		return weekdayHeight * 7;
	}

	return 0;
}

/**
 * Filtering slots by partial render indexes
 * @deprecated - might be removed in the future
 */
export const filterSlots = (slots: ISlotsEventsAndEventsWithSpaces, from: number, to: number) => {
	if (slots.events && slots.eventsToSpaces && !Number.isNaN(to)) {
		const events = {};
		let eventsToSpaces = {};
		Object.values(slots.events).forEach(event => {
			const start = dayjs(event.startDate).hour();
			const end = dayjs(event.endDate).hour();
			if (end >= from && start <= to) {
				events[Number(event.id)] = event;

				if (Array.isArray(eventsToSpaces[Number(event.spaceId)])) {
					eventsToSpaces[Number(event.spaceId)].push(event);
				} else {
					eventsToSpaces = {
						...eventsToSpaces,
						[Number(event.spaceId)]: [event],
					};
				}
			}
		});

		return { events, eventsToSpaces };
	}

	return slots;
};

/**
 * Calculating the position of the modal based on the container and box
 * @param box {ElementHTML} - the box element
 * @param container {ElementHTML} - the container element
 * @param boxW {ElementHTML} - the width of the box element
 * @param containerW {ElementHTML} - the width of the container element
 * @return {{ x: string; y: string }} - the position of the modal
 */
export function calculateModalPosition(
	box: ElementHTML,
	container: ElementHTML,
	boxW: ElementHTML,
	containerW: ElementHTML
): { x: string; y: string } {
	const result = {
		y: 'top: 0;',
		x: 'left: 50%;',
	};

	if (container && box) {
		if (container.offsetHeight - box.offsetTop >= 460) {
			result.y = 'top: 0;';
		} else if (box.offsetTop >= 460) {
			result.y = 'bottom: 0;';
		} else {
			const top = box.offsetTop - (container.offsetHeight - 460) / 2;
			result.y = `top: -${top}px;`;
		}
	}

	if (containerW && boxW) {
		if (containerW.offsetWidth - boxW.offsetLeft >= 275) {
			result.x = 'left: 50%;';
		} else if (boxW.offsetLeft >= 275) {
			result.x = 'right: 50%;';
		} else {
			const left = boxW.offsetLeft - (containerW.offsetWidth - 275) / 2;
			result.x = `left: -${left}px;`;
		}
	}

	return result;
}

/**
 * Removing slots what started or ended not on selected (or current) date
 * @deprecated - might be removed in the future
 */
export const filterSlotsForDailyView = (slots: ISlotEventsToSpaces, date?: string): ISlotEventsToSpaces => {
	const initialIndex =
		Object.keys(slots).length && dayjs(Object.keys(slots)[0], 'YYYY-MM-DD').isValid()
			? Object.keys(slots).findIndex(date => dayjs(date).isSame(dayjs(date)))
			: -1;
	const filteredDataSlots: [string, any][] = initialIndex !== -1 ? Object.entries(slots)[initialIndex][1] : slots;
	const eventsToSpaces = {} as ISlotEventsToSpaces;
	Object.entries(filteredDataSlots).forEach(([spaceId, events]) => {
		eventsToSpaces[Number(spaceId)] =
			events?.filter((event: ISlot) => {
				const earliestStartDate = chain([event, ...(event.children ?? [])])
					.map(slot => slot.startDate)
					.min()
					.value();
				const latestEndDate = chain([event, ...(event.children ?? [])])
					.map(slot => slot.endDate)
					.max()
					.value();
				return isDateBetweenDates(date ?? new Date().toISOString(), earliestStartDate, latestEndDate);
			}) ?? [];
	});

	return eventsToSpaces;
};

/**
 * Removing slots what started before selected (or current) date
 * @deprecated - might be removed in the future
 * */
export const filterSlotsForOldSlots = (slots: ISlotEventsToSpaces, date?: string) => {
	const eventsToSpaces = {} as ISlotEventsToSpaces;
	Object.entries(slots).forEach(([spaceId, events]) => {
		eventsToSpaces[Number(spaceId)] = events.filter((event: ISlot) =>
			isDateBetweenDates(date ?? new Date().toISOString(), event.startDate, event.endDate)
		);
	});

	return eventsToSpaces;
};

export function calculateNewDate(date: string, minutesDifference: number): string {
	if (minutesDifference) {
		return dayjs(date).add(minutesDifference, 'minute').toISOString();
	}

	return dayjs(date).subtract(Math.abs(minutesDifference), 'minute').toISOString();
}

/**
 * Calculate how many “steps” of minutes separate a given time from a base time,
 * optionally summing them back together.
 *
 * @param date 			- An ISO-8601 timestamp (or any string parsable by dayjs)
 *                   whose minute-of-hour will be used as the base.
 * @param minutes  - The minute value you want to align to the nearest step.
 * @param step     - The granularity in minutes to snap to (default: 5).
 * @param sum      - If true, returns the snapped difference **plus** the base minutes;
 *                   otherwise returns only the snapped difference (default: false).
 * @returns        - If `sum` is false: the adjusted difference in minutes,
 *                   such that `(baseMinutes + returned) % step === 0`.
 *                   If `sum` is true: `(baseMinutes + returned)`.
 *
 * @example
 * // Suppose date has minute = 12, step = 5:
 * // baseMinutes = 12 → nearest multiples of 5 are 10 or 15.
 * // startMinutes % step = 2 → since 2 <= step / 2, we subtract:
 * const diff = getMinutesDifferenceByStep('2025-05-16T10:12:00Z', 0, 5);
 * // diff = -2, because 12 + (-2) = 10
 *
 * @example
 * // If you want the total minutes from midnight rounded to step:
 * // minutes = hours * 60 + yourMinuteOffset
 * // and sum = true to include the date’s minute:
 * const total = getMinutesDifferenceByStep('2025-05-16T10:12:00Z', 10 * 60 + 3, 5, true);
 * // baseMinutes = 12, initial minutes = 603
 * // (603 + 12) = 615 → already a multiple of 5, so returns 615
 */
export function getMinutesDifferenceByStep(date: string, minutes: number, step: number = 5, sum?: boolean): number {
	let startMinutes: number = dayjs(date).minute();

	while (startMinutes > step) startMinutes -= step;

	while ((minutes + startMinutes) % step !== 0) {
		if (startMinutes % step > step / 2) {
			minutes += 1;
		} else {
			minutes -= 1;
		}
	}

	if (sum) {
		return minutes + startMinutes;
	}

	return minutes;
}

/*
   @param pos: the y-position to scroll to (in pixels)
   @param time: the exact amount of time the scrolling will take (in milliseconds)
*/
export function scrollToVertical(container: HTMLDivElement, destination: number, time = 500) {
	const from = container.scrollTop;
	let start = 0;

	window.requestAnimationFrame(function step(currentTime) {
		start = !start ? currentTime : start;
		const progress = currentTime - start;
		if (from < destination) {
			container.scrollTo(container.scrollLeft, ((destination - from) * progress) / time + from);
		} else {
			container.scrollTo(container.scrollLeft, from - ((from - destination) * progress) / time);
		}
		if (progress < time) {
			window.requestAnimationFrame(step);
		} else {
			container.scrollTo(container.scrollLeft, destination);
		}
	});
}

export function scrollToHorizontal(container: HTMLDivElement, destination: number, time = 500) {
	const from = container.scrollLeft;
	let start = 0;

	window.requestAnimationFrame(function step(currentTime) {
		start = !start ? currentTime : start;
		const progress = currentTime - start;
		if (from < destination) {
			container.scrollTo(((destination - from) * progress) / time + from, container.scrollTop);
		} else {
			container.scrollTo(from - ((from - destination) * progress) / time, container.scrollTop);
		}
		if (progress < time) {
			window.requestAnimationFrame(step);
		} else {
			container.scrollTo(destination, container.scrollTop);
		}
	});
}

/**
 * Sorts weekly day-list slots by the displayed clock times (`startTime` / `endTime`),
 * then id. Do not use `overall*` here — those are maintenance-expanded windows and are
 * often midnight→end-of-day (or otherwise shifted), which breaks chronological order.
 */
function sortSlotsByTimes(slot: ISlot, other: ISlot): number {
	const startTimeDiff: number =
		Number(dayjs(slot.startTime, DateTimeFormats.H24_WITH_SECONDS)) -
		Number(dayjs(other.startTime, DateTimeFormats.H24_WITH_SECONDS));

	const endTimeDiff: number =
		Number(dayjs(slot.endTime, DateTimeFormats.H24_WITH_SECONDS)) -
		Number(dayjs(other.endTime, DateTimeFormats.H24_WITH_SECONDS));

	return startTimeDiff || endTimeDiff || String(slot.id).localeCompare(String(other.id));
}

/**
 * Grouping slots by date, then sorting them by the times
 * @param dates {dayjs.Dayjs[]} - array of dates to group by
 * @param slots {ISlot[]} - array of slots to group
 * @return {ISlotDateGroup[]} - array of grouped slots
 */
export function groupSlotsByDate(dates: dayjs.Dayjs[], slots: ISlot[]): ISlotDateGroup[] {
	const result: ISlotDateGroup[] = [];
	for (const date of dates) {
		const filteredSlots: ISlot[] = slots
			?.filter((event: ISlot) => date.isSame(dayjs.utc(event.startDate).startOf(ETimes.DAY), ETimes.DAY))
			?.sort(sortSlotsByTimes);

		const weekDay: ISlotDateGroup = {
			date,
			slots: filteredSlots ?? [],
		};

		result.push(weekDay);
	}
	return result;
}

/**
 * Calculating the number of slots by duration.
 * The duration is the time in minutes of each slot.
 * For example, if the duration is 15 minutes,
 * there will be 96 slots in a day (24 hours * 60 minutes / 15 minutes).
 * @param duration {number} - duration of the slot in minutes
 */
export function getNumberOfSlotsByDuration(duration: number) {
	return (HOURS_PER_DAY * MINUTES_PER_HOUR) / duration;
}

/**
 * Calculating the size of each slot in pixels.
 * The size is the height of the slot in pixels.
 * For example, if the height of the day is 600 pixels,
 * and there are 24 slots in a day, the size of each slot will be 25 pixels (600 pixels / 24 slots).
 * @param hourSize {number} - height of the day in pixels
 * @param count {number} - number of slots in a day
 * @return {number} - size of each slot in pixels
 */
export function getSizeOfSlots(hourSize: number, count: number): number {
	return Math.floor((HOURS_PER_DAY * hourSize) / count);
}

/**
 * Returns an array of dates that overlap with the given slot.
 * @param slot {ISlot} - The slot for which to get the overlap dates.
 * @return {string[]} - An array of dates in 'YYYY-MM-DD' format that overlap with the slot.
 */
export function getSlotOverlapDates(slot: ISlot): string[] {
	const start: dayjs.Dayjs = dayjs(slot.startDate);
	const end: dayjs.Dayjs = dayjs(slot.endDate);

	const dates: string[] = [];
	for (let current = start; current.isBefore(end) || current.isSame(end, 'date'); current = current.add(1, 'day')) {
		dates.push(current.format(DateTimeFormats.YYYY_MM_DD));
	}

	return dates;
}

/**
 * Calculates the overall start and end times for a slot, including its children.
 * @param slot {ISlot} - The slot for which to calculate the overall times.
 * @param date {string} - The date in 'YYYY-MM-DD' format to use for the calculation.
 * @return {ISlot} - The updated slot with overallStartTime and overallEndTime properties set.
 */
export function calculateOverallSlotTimes(slot: ISlot, date: string): ISlot {
	const startTimes: string[] = [
		getCombinedDateTimeString(slot.startDate, slot.startTime),
		...(slot.children?.map(child => getCombinedDateTimeString(child.startDate, child.startTime)) ?? []),
	];
	const endTimes: string[] = [
		getCombinedDateTimeString(slot.endDate, slot.endTime),
		...(slot.children?.map(child => getCombinedDateTimeString(child.endDate, child.endTime)) ?? []),
	];

	slot.overallStartTime = calculateOverallStartTime(startTimes, slot.startDate, date);
	slot.overallEndTime = calculateOverallEndTime(endTimes, slot.endDate, date);

	return slot;
}

/**
 * Finds a resource by id in the resources tree
 * @param resources {IResource[]} - resources tree
 * @param resourceId {number} - resource id to find
 * @return {IResource} - found resource
 */
export const findResource = (resources: IResource[], resourceId: number): IResource | null => {
	for (const resource of resources) {
		if (resource.id === resourceId) {
			return resource;
		}

		if (resource.children) {
			const foundResource: IResource | null = findResource(resource.children, resourceId);
			if (foundResource) {
				return foundResource;
			}
		}
	}

	return null;
};
